import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getNotifications } from "../../redux/slices/MediaSlice";
import "./Notification.css";

const Notification = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications, notificationsLoading, hasMoreNotifications } = useSelector(
    (state) => state.media
  );
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;
  const userId = user?.UserID;

  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    setExpanded((prevExpanded) => {
      const currentLength = notifications.length;
      let newExpanded = [...prevExpanded];
      while (newExpanded.length < currentLength) {
        newExpanded.push(false);
      }
      if (newExpanded.length > currentLength) {
        newExpanded = newExpanded.slice(0, currentLength);
      }
      return newExpanded;
    });
  }, [notifications.length]);

  useEffect(() => {
    if (userId && open) {
      dispatch(getNotifications({ userId, Seen: 1, pageSize: 10, pageId: 1 }));
    }
  }, [dispatch, userId, open]);

  const loadMore = useCallback(() => {
    const nextPage = Math.floor(notifications.length / 10) + 1;
    dispatch(getNotifications({ userId, Seen: 1, pageSize: 10, pageId: nextPage }));
  }, [dispatch, userId, notifications.length]);

  const handleNavigation = useCallback((vendorId, imageId) => {
    navigate(`/enlargeImageView?vendorid=${vendorId}&imageid=${imageId}`);
    onClose(); // Close the notification
  }, [navigate, onClose]);

  // Map API data to component structure
  const mappedNotifications = notifications.map((item) => {
    const fullName = `${item.firstName} ${item.lastName}`;
    let action;
    if (item.notifyType === "like") {
      action = "liked your post";
    } else if (item.notifyType === "comment") {
      action = `commented: "${item.content}"`;
    } else {
      action = "interacted with your post"; // Fallback
    }

    // Simple relative time formatter
    const createdDate = new Date(item.createdAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now - createdDate) / (1000 * 60));
    let time;
    if (diffInMinutes < 1) {
      time = "Just now";
    } else if (diffInMinutes < 60) {
      time = `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      time = `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      time = `${Math.floor(diffInMinutes / 1440)}d ago`;
    }

    return {
      id: item.notifyId,
      userAvatar:
        item.userPhoto ||
        "https://st4.depositphotos.com/6672868/20448/v/600/depositphotos_204485038-stock-illustration-user-vector-icon-simple-illustration.jpg",
      fullName,
      action,
      time,
      postImage: item.imageUrl,
      createdBy: item.createdBy,
      imageId: item.imageId,
    };
  });

  const toggleExpanded = useCallback((index) => {
    setExpanded((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  }, []);

  // Skeleton loader component
  const SkeletonNotification = () => (
    <div className="notif-item skeleton">
      <div className="user-avatar skeleton-circle"></div>
      <div className="notif-content">
        <div className="text-with-toggle">
          <div className="notif-text skeleton-line"></div>
          <div className="toggle-btn skeleton-line small"></div>
        </div>
        <div className="notif-time skeleton-line small"></div>
      </div>
      <div className="post-preview skeleton-image"></div>
    </div>
  );

  if (!open) {
    return null;
  }

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="notif-sheet">
        <div className="notif-header">
          <h3>Notifications</h3>
          <span className="close-btn" onClick={onClose}>
            ✕
          </span>
        </div>
        <div className="notif-list">
          {notificationsLoading && notifications.length === 0 ? (
            <>
              {[...Array(10)].map((_, index) => (
                <SkeletonNotification key={index} />
              ))}
            </>
          ) : (
            <>
              {mappedNotifications.map((item, index) => {
                const fullText = `${item.fullName} ${item.action}`;
                const isLongText = fullText.length > 30;
                const isExpanded = expanded[index];
                const displayAction =
                  isLongText && !isExpanded
                    ? item.action.substring(0, 30 - item.fullName.length - 1) + "..."
                    : item.action;

                return (
                  <div 
                    key={item.id} 
                    className="notif-item"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleNavigation(item.createdBy, item.imageId)}
                  >
                    <img
                      src={item.userAvatar}
                      alt="User avatar"
                      className="user-avatar"
                    />
                    <div className="notif-content">
                      <div className="text-with-toggle">
                        <span className="notif-text">
                          <strong>{item.fullName}</strong> {displayAction}
                        </span>
                     
                      </div>
                      <div className="d-flex justify-content-between">
                      <span className="notif-time">{item.time}</span>
                         {isLongText && (
                          <span
                            className="toggle-btn"
                            onClick={(e) => {
                              e.stopPropagation();  
                              toggleExpanded(index);
                            }}
                          >
                            {isExpanded ? " Less" : " More"}
                          </span>
                        )}
                      </div>
                
                    </div>
                    {item.postImage && (
                      <img
                        src={item.postImage}
                        alt="Post preview"
                        className="post-preview"
                      />
                    )}
                  </div>
                );
              })}
              {hasMoreNotifications && notifications.length > 0 && (
                <span
                  className="show-more-btn-notifications"
                  onClick={loadMore}
                  disabled={notificationsLoading}
                >
                  {notificationsLoading ? "Loading..." : "Show more"}
                </span>
              )}
              {notifications.length > 0 && !hasMoreNotifications && !notificationsLoading && (
                <div className="no-more-text">No more to show</div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Notification;